import { Vector3 } from 'three'
import { blue, red } from '@mui/material/colors'
import type { InnerForcesEnvelopeDTO } from '@api/fastAPI.schemas'
import { calculateText3DLabelRectangleHeight } from 'src/components/scene/TextSprite/utils'
import { formatTwoDecimals, roundsToZeroInTwoDecimals } from 'src/utils/numberFormatters'

export type EnvelopeComponent = 'moment_y' | 'shear_z' | 'displacement_z'

export const envelopeComponentLabels: Record<EnvelopeComponent, string> = {
  moment_y: 'My',
  shear_z: 'Vz',
  displacement_z: 'uz',
}

export const envelopeComponentUnits: Record<EnvelopeComponent, string> = {
  moment_y: 'kNm',
  shear_z: 'kN',
  displacement_z: 'mm',
}

/** Curve colors of the 3D envelope (as in the inner forces plot). */
export const ENVELOPE_MAX_COLOR = blue[600]
export const ENVELOPE_MIN_COLOR = red[600]

/** Border width of the extreme value labels (m), as on the 3D line load labels. */
export const ENVELOPE_LABEL_BORDER_WIDTH = 0.005

/** Distance (m) between the extreme value labels and the curve point they belong to. */
export const ENVELOPE_LABEL_CURVE_GAP = 0.05

/** Free space between stacked label boxes (m), on top of their borders. */
const ENVELOPE_LABEL_STACK_GAP = 0.01

/** Most value labels stacked on one extreme (the two sides of a jump across zero). */
const MAX_STACKED_EXTREME_LABELS = 2

/** Font size of the diagram title relative to the extreme value labels. */
export const ENVELOPE_TITLE_SCALE_FACTOR = 2

/** Distance (m) between stacked extreme value labels: box height plus border and gap. */
export const getExtremeLabelSpacing = (textScale: number): number =>
  calculateText3DLabelRectangleHeight(1, textScale) +
  ENVELOPE_LABEL_BORDER_WIDTH +
  ENVELOPE_LABEL_STACK_GAP

/**
 * Anchor (bottom center) of the diagram title: above the middle of the member, over the top of
 * the plot frame spanned by `maxOrdinate`, clear of the extreme value labels that can stack on it.
 */
export const getEnvelopeTitlePosition = (
  axis: [Vector3, Vector3],
  ordinateDirection: Vector3,
  maxOrdinate: number,
  textScale: number,
): Vector3 => {
  const offset =
    maxOrdinate +
    ENVELOPE_LABEL_CURVE_GAP +
    MAX_STACKED_EXTREME_LABELS * getExtremeLabelSpacing(textScale) +
    ENVELOPE_LABEL_CURVE_GAP
  return axis[0].clone().lerp(axis[1], 0.5).addScaledVector(ordinateDirection, offset)
}

const EPSILON = 1e-9

export interface EnvelopeCurve {
  /** Polyline from the axis at the first station along the curve back to the axis at the last. */
  outline: Vector3[]
  /** Triangle list (3 points per triangle) of the area between the member axis and the curve. */
  fillTriangles: Vector3[]
}

export interface EnvelopeExtreme {
  /**
   * Curve point the labels are anchored at; at a jump the end with the larger magnitude, so the
   * labels clear the vertical jump line.
   */
  position: Vector3
  /** Station of the extreme along the member axis (0 at the start, 1 at the end). */
  relativePosition: number
  /** The labelled value with the largest magnitude (the one the labels are anchored at). */
  value: number
  /** Values to label, high to low: two only where a line jumps across zero (one per sign). */
  labelValues: number[]
  /** Whether the ordinate is drawn above the member axis (labels then sit on top of it). */
  isAboveAxis: boolean
}

export interface EnvelopeGeometry {
  axis: [Vector3, Vector3]
  /** Unit vector the ordinates are drawn along: perpendicular to the axis, in its vertical plane. */
  ordinateDirection: Vector3
  max: EnvelopeCurve
  min: EnvelopeCurve
  /**
   * Labelled values of each line, at most one entry per station: at the member ends and supports
   * (unless the other line has a larger value of equal sign there), at jumps (the larger side, or
   * one value per sign of both lines where a line crosses zero) and at the largest strict extremum
   * inside each field (unless the other line's extremum on the same spot is larger with equal
   * sign). Values printing as 0 and red values duplicating blue ones are left out.
   */
  maxExtremes: EnvelopeExtreme[]
  minExtremes: EnvelopeExtreme[]
}

/**
 * Upward unit normal of the (unit) member direction within its vertical plane: global z with its
 * component along the member removed. A vertical member has no such plane; its diagram then lies
 * in the global xz plane.
 */
const getOrdinateDirection = (direction: Vector3): Vector3 => {
  const up = new Vector3(0, 0, 1).addScaledVector(direction, -direction.z)
  return up.lengthSq() < EPSILON ? new Vector3(1, 0, 0) : up.normalize()
}

/**
 * Builds the 3D envelope diagram of one inner force component (or the deflection) along the
 * member axis. Ordinates
 * are drawn perpendicular to the axis in its vertical plane (straight up for horizontal members,
 * tilted with inclined ones) and normalized per member, so the largest one is `maxOrdinate` (m).
 * Sign convention as in the inner forces plot: positive moments and deflections hang below the
 * axis, positive shear forces point up. Repeated stations (jumps at supports or
 * point loads) become outline segments along the ordinate. Returns `null` when there is nothing
 * to draw (no stations, zero-length member or an all-zero envelope).
 */
export const buildEnvelopeGeometry = (
  envelope: InnerForcesEnvelopeDTO,
  component: EnvelopeComponent,
  maxOrdinate: number,
): EnvelopeGeometry | null => {
  const { relative_positions: positions } = envelope
  const { y_min: yMin, y_max: yMax } = envelope[component]
  if (positions.length < 2 || yMin.length !== positions.length) return null
  if (yMax.length !== positions.length) return null

  const start = new Vector3(envelope.start.x, envelope.start.y, envelope.start.z)
  const end = new Vector3(envelope.end.x, envelope.end.y, envelope.end.z)
  const memberLength = start.distanceTo(end)
  if (memberLength < EPSILON) return null

  const maxAbsValue = Math.max(...yMin.map(Math.abs), ...yMax.map(Math.abs))
  if (maxAbsValue < EPSILON) return null

  const sign = component === 'shear_z' ? 1 : -1
  const ordinateScale = (sign * maxOrdinate) / maxAbsValue

  const ordinateDirection = getOrdinateDirection(end.clone().sub(start).normalize())

  const axisPoint = (relativePosition: number) => start.clone().lerp(end, relativePosition)
  const curvePoint = (relativePosition: number, value: number) =>
    axisPoint(relativePosition).addScaledVector(ordinateDirection, value * ordinateScale)

  const buildCurve = (values: number[]): EnvelopeCurve => {
    const curvePoints = positions.map((position, index) => curvePoint(position, values[index]))
    const outline = [
      axisPoint(positions[0]),
      ...curvePoints,
      axisPoint(positions[positions.length - 1]),
    ]

    const fillTriangles: Vector3[] = []
    for (let index = 0; index < positions.length - 1; index++) {
      const position0 = positions[index]
      const position1 = positions[index + 1]
      if (position1 - position0 < EPSILON) continue

      const value0 = values[index]
      const value1 = values[index + 1]
      const base0 = axisPoint(position0)
      const base1 = axisPoint(position1)
      const top0 = curvePoints[index]
      const top1 = curvePoints[index + 1]

      if (value0 * value1 < 0) {
        // split at the zero crossing so the area never self-intersects
        const zeroPosition = position0 + (value0 / (value0 - value1)) * (position1 - position0)
        const zeroPoint = axisPoint(zeroPosition)
        fillTriangles.push(base0, top0, zeroPoint, zeroPoint, top1, base1)
      } else {
        fillTriangles.push(base0, top0, top1, base0, top1, base1)
      }
    }

    return { outline, fillTriangles }
  }

  const makeExtreme = (relativePosition: number, labelValues: number[]): EnvelopeExtreme => {
    const value = largestMagnitude(labelValues)
    return {
      position: curvePoint(relativePosition, value),
      relativePosition,
      value,
      labelValues,
      isAboveAxis: value * ordinateScale >= 0,
    }
  }

  const lines = [yMax, yMin]
  const stations = groupStations(positions)
  const fieldEnds = getFieldEnds(envelope.support_relative_positions)
  const isMemberEnd = (position: number) => position < EPSILON || position > 1 - EPSILON
  const isSupport = (position: number) =>
    fieldEnds.some(fieldEnd => Math.abs(fieldEnd - position) < EPSILON)

  // values left and right of a station; they differ where the line jumps (e.g. at a support or
  // point load), the repeated stations at the member ends only close the line to the axis
  const sidesAt = (station: Station, values: number[]): [number, number] => [
    values[station.first],
    values[station.last],
  ]
  const isJump = (station: Station, values: number[]) => {
    const [left, right] = sidesAt(station, values)
    return !isMemberEnd(station.position) && formatTwoDecimals(left) !== formatTwoDecimals(right)
  }
  // value of a station without a jump: at the member ends the one inside the member
  const valueAt = (station: Station, values: number[]) => {
    if (station.position < EPSILON) return values[station.last]
    if (station.position > 1 - EPSILON) return values[station.first]
    return largestMagnitude(sidesAt(station, values))
  }

  // strict extremum inside each field: the largest local peak or valley of the line strictly
  // between the field ends and off any jump
  const fieldExtremumStations = (values: number[]): Set<number> => {
    const extremumStations = new Set<number>()
    fieldEnds.slice(1).forEach((fieldEnd, fieldIndex) => {
      const fieldStart = fieldEnds[fieldIndex]
      let best = -1
      stations.forEach((station, index) => {
        const isInside =
          station.position > fieldStart + EPSILON && station.position < fieldEnd - EPSILON
        if (!isInside || isJump(station, values)) return
        const value = valueAt(station, values)
        const previous = values[stations[index - 1].last]
        const next = values[stations[index + 1].first]
        const isPeak = (value >= previous && value > next) || (value > previous && value >= next)
        const isValley = (value <= previous && value < next) || (value < previous && value <= next)
        if (!isPeak && !isValley) return
        if (best < 0 || Math.abs(value) > Math.abs(valueAt(stations[best], values))) best = index
      })
      if (best >= 0) extremumStations.add(best)
    })
    return extremumStations
  }
  const extremumStations = lines.map(fieldExtremumStations)

  // label values per line and station
  const labels = lines.map(() => stations.map((): number[] => []))
  stations.forEach((station, index) => {
    const sides = lines.map(values => sidesAt(station, values))
    const jumps = lines.map(values => isJump(station, values))

    // a jump across zero: the largest positive and the largest negative value of both lines on
    // this spot, each labelled on its own line
    if (lines.some((_, line) => jumps[line] && hasOppositeSigns(sides[line]))) {
      const candidates = sides.flatMap((lineSides, line) =>
        lineSides.map(value => ({ line, value })),
      )
      const positive = candidates.reduce<typeof candidates[number] | null>(
        (best, candidate) =>
          candidate.value > 0 && (!best || candidate.value > best.value) ? candidate : best,
        null,
      )
      const negative = candidates.reduce<typeof candidates[number] | null>(
        (best, candidate) =>
          candidate.value < 0 && (!best || candidate.value < best.value) ? candidate : best,
        null,
      )
      for (const candidate of [positive, negative]) {
        if (candidate) labels[candidate.line][index].push(candidate.value)
      }
      return
    }

    lines.forEach((values, line) => {
      // a jump keeping its sign: its larger side
      if (jumps[line]) {
        labels[line][index].push(largestMagnitude(sides[line]))
        return
      }
      const value = valueAt(station, values)
      // member ends and supports, unless the other line has a larger value of equal sign there
      if (isMemberEnd(station.position) || isSupport(station.position)) {
        const other = 1 - line
        const otherValues = jumps[other] ? sides[other] : [valueAt(station, lines[other])]
        const isCoveredByOther = otherValues.some(
          otherValue => otherValue * value > 0 && Math.abs(otherValue) > Math.abs(value),
        )
        if (!isCoveredByOther) labels[line][index].push(value)
        return
      }
      // strict extrema inside the fields; where both lines have one here with equal sign, only the
      // larger (the blue one if equal)
      if (!extremumStations[line].has(index)) return
      const other = 1 - line
      const otherValue = valueAt(station, lines[other])
      const isCoveredByOther =
        extremumStations[other].has(index) &&
        otherValue * value > 0 &&
        (Math.abs(otherValue) > Math.abs(value) ||
          (line === 1 && Math.abs(otherValue) === Math.abs(value)))
      if (!isCoveredByOther) labels[line][index].push(value)
    })
  })

  // values printing as 0 get no label, and a red value printing like a blue one on the same spot
  // is left to the blue label
  const printed = (values: number[]) => values.map(formatTwoDecimals)
  const toExtremes = (line: number): EnvelopeExtreme[] =>
    stations.flatMap((station, index) => {
      const labelValues = labels[line][index]
        .filter(value => !roundsToZeroInTwoDecimals(value))
        .filter(
          value => line === 0 || !printed(labels[0][index]).includes(formatTwoDecimals(value)),
        )
        .sort((a, b) => b - a)
      return labelValues.length > 0 ? [makeExtreme(station.position, labelValues)] : []
    })
  const maxExtremes = toExtremes(0)
  const minExtremes = toExtremes(1)

  return {
    axis: [start, end],
    ordinateDirection,
    max: buildCurve(yMax),
    min: buildCurve(yMin),
    maxExtremes,
    minExtremes,
  }
}

/** The value with the largest magnitude (the first of equal ones). */
const largestMagnitude = (values: number[]): number =>
  values.reduce((largest, value) => (Math.abs(value) > Math.abs(largest) ? value : largest))

/** Whether the values include a positive and a negative one (values printing as 0 have no sign). */
const hasOppositeSigns = (values: number[]): boolean => {
  const signed = values.filter(value => !roundsToZeroInTwoDecimals(value))
  return signed.some(value => value > 0) && signed.some(value => value < 0)
}

/**
 * Ends of the member's fields as relative positions: the member ends and the supports (sorted,
 * coinciding ones merged).
 */
const getFieldEnds = (supportRelativePositions: number[]): number[] =>
  [0, ...supportRelativePositions, 1]
    .map(position => Math.min(1, Math.max(0, position)))
    .sort((a, b) => a - b)
    .filter((position, index, sorted) => index === 0 || position - sorted[index - 1] > EPSILON)

/** A station along the member; a jump repeats it, from index `first` (left) to `last` (right). */
interface Station {
  position: number
  first: number
  last: number
}

/** Stations of the (sorted) relative positions, repeated positions grouped into one. */
const groupStations = (positions: number[]): Station[] => {
  const stations: Station[] = []
  positions.forEach((position, index) => {
    const previous = stations[stations.length - 1]
    if (previous && position - previous.position < EPSILON) previous.last = index
    else stations.push({ position, first: index, last: index })
  })
  return stations
}

export interface StackedExtremeLabel {
  value: number
  position: Vector3
}

/**
 * Positions of an extreme's value labels, stacked along global z like the stacked load labels,
 * always with the higher value on top, `curveGap` away from the curve point: above the curve the
 * lowest box sits above the curve point and the others stack upwards, below it the highest box
 * hangs below it and the others stack downwards. `labelSpacing` is the box height plus the gap
 * between boxes; the z offsets are stretched so that labels tilted with an inclined member (their
 * y axis is `textYDirection`) keep these distances.
 */
export const stackExtremeLabels = (
  extreme: EnvelopeExtreme,
  labelSpacing: number,
  textYDirection: Vector3,
  curveGap: number,
): StackedExtremeLabel[] => {
  const stretch = textYDirection.z > EPSILON ? 1 / textYDirection.z : 1
  const count = extreme.labelValues.length
  return extreme.labelValues.map((value, index) => {
    const offset = extreme.isAboveAxis
      ? curveGap + (count - 1 - index) * labelSpacing
      : -curveGap - index * labelSpacing
    return {
      value,
      position: extreme.position.clone().setZ(extreme.position.z + offset * stretch),
    }
  })
}

/** Flat xyz position array (e.g. for a `position` buffer attribute) of the given points. */
export const toPositionArray = (points: Vector3[]): Float32Array =>
  new Float32Array(points.flatMap(point => point.toArray()))
